const BASE_GRID = {
  eyebrow: "Room rates",
  title: "Set up your room rates in a practical grid",
  copy: "Create each rate from a guided modal, then review everything from a compact descriptive grid before saving.",
  noCategoriesTitle: "Complete room categories first",
  noCategoriesCopy: "We need room categories before preparing room rates.",
  add: "Add rate",
  edit: "Edit",
  remove: "Remove",
  rateCode: "Code",
  rate: "Rate",
  name: "Rate name",
  roomCategory: "Room category",
  client: "Agency or OTA",
  currency: "Currency",
  adults: "Adults",
  adultsRule: "Rule",
  children: "Children",
  childrenRule: "Rule",
  maxOccupancy: "Max occupancy",
  maxOccupancyRule: "Rule",
  minPriceAmount: "Minimum price",
  maxPriceAmount: "Maximum price",
  defaultMinStay: "Default min stay",
  defaultMaxStay: "Default max stay",
  occupancy: "Occupancy",
  stayRules: "Stay rules",
  visibility: "Visibility",
  policy: "Policy",
  actions: "Actions",
  refundable: "Refundable",
  showInBookingEngine: "Booking engine",
  cancellationPolicy: "Cancellation policy",
  penaltyType: "Penalty type",
  penaltyAmount: "Penalty amount",
  penaltyDescription: "Penalty description",
  roomCategoryPlaceholder: "Choose category",
  clientPlaceholder: "No Agency or OTA",
  currencyPlaceholder: "Choose currency",
  rateCodePlaceholder: "BAR",
  namePlaceholder: "Best Available Rate",
  minStayPlaceholder: "No default",
  maxStayPlaceholder: "No default",
  minPricePlaceholder: "No limit",
  maxPricePlaceholder: "No limit",
  adultsAny: "Any",
  childrenAny: "Any",
  maxOccupancyAny: "Any",
  matchExact: "Exact",
  matchUpto: "Up to",
  refundableYes: "Yes",
  refundableNo: "No",
  bookingEngineVisible: "Visible",
  bookingEngineHidden: "Hidden",
  penaltyTypeNone: "None",
  penaltyTypeFixed: "Fixed",
  penaltyTypePercent: "Percent",
  emptyTitle: "Start with your first rate",
  emptyCopy: "Examples: BAR, Non-refundable or Bed and Breakfast.",
  createModalTitle: "Create a room rate",
  createModalCopy: "Fill in the main details of the rate here, then add it to the grid.",
  editModalTitle: (name) => `Edit ${name || "this rate"}`,
  editModalCopy: "Review and adjust the details of this rate here.",
  createModalHint: "",
  createModalCancel: "Cancel",
  createModalConfirm: "Add to grid",
  editModalConfirm: "Update rate",
  sectionBasics: "Basics",
  sectionBasicsCopy: "Code, name, category, agency or OTA, and currency.",
  sectionVisibility: "Visibility",
  sectionVisibilityCopy: "Control booking-engine exposure for the permanent rate.",
  sectionVisibilityHelp:
    "Rates created here are permanent. We save them with today's date as valid from and 31/12/2099 as valid to. Temporary rates can be created later.",
  sectionVisibilityHelpAria: "More information about permanent room rates",
  bookingEngineHelpAria: "More information about booking engine visibility",
  bookingEngineHelp:
    "If this rate is visible, it can be sold on your website through the booking engine.\n\nIf it is not visible, it will not be sold on your website.\n\nRecommendation: if this rate is meant for an agency or OTA, set it as not visible so it does not end up being sold on your website at an agency or OTA price.",
  sectionOccupancy: "Occupancy limits",
  sectionOccupancyCopy: "Define adults, children and max occupancy by pairing the number with its matching rule.",
  adultsHelpAria: "More information about adults rules",
  adultsHelp:
    "Use this to decide how the rate should match adult occupancy inside the selected room category.\n\nChoose the number and then whether it must be exact or apply up to that value.\n\nLeave it as Any if this rate can be sold for any valid adult occupancy allowed by the room category.",
  childrenHelpAria: "More information about children rules",
  childrenHelp:
    "Use this to decide how the rate should match child occupancy inside the selected room category.\n\nChoose the number and then whether it must be exact or apply up to that value.\n\nLeave it as Any if this rate can be sold for any valid children occupancy allowed by the room category.",
  maxOccupancyHelpAria: "More information about total occupancy rules",
  maxOccupancyHelp:
    "Use this when the rate should depend on the total number of guests, counting adults and children together.\n\nChoose the number and then whether it must be exact or apply up to that value.\n\nLeave it as Any if the rate can be sold for any valid combined occupancy allowed by the room category.",
  sectionPriceAndStay: "Price and stay",
  sectionPriceAndStayCopy: "Set price thresholds and choose one stay-rule method for the permanent rate.",
  minPriceHelpAria: "More information about minimum price",
  minPriceHelp:
    "This is the minimum price you want to sell at.\n\nAny price below this limit will not pass and will be adjusted automatically up to this minimum.",
  maxPriceHelpAria: "More information about maximum price",
  maxPriceHelp:
    "This is the maximum price you want to sell at.\n\nAny price above this limit will not pass and will be adjusted automatically down to this maximum.",
  sectionPolicies: "Policies",
  sectionPoliciesCopy: "Add cancellation and penalty details here if you want to leave them ready now.",
  stayModeDefault: "Use default min/max stay",
  stayModeWeekday: "Use weekday min/max stay",
  stayModeHint: "Choose one method only: a single default pair or weekday-specific rules.",
  weekdayMinShort: "Min",
  weekdayMaxShort: "Max",
  weekdayFillMin: "Fill min from hidden defaults",
  weekdayFillMax: "Fill max from hidden defaults",
  weekdayClear: "Clear weekdays",
  addSuccess: "The rate was added to the grid. Remember to save when you are ready.",
  editSuccess: "The rate was updated in the grid. Remember to save when you are ready.",
  loadError: "We could not load the saved room rates.",
  saveError: "We could not save the room rates right now.",
  saveSuccess: "Your room rates draft has been saved.",
  createValidationRateCodeRequired: "The new rate needs a code.",
  createValidationRateCodeLength: "The new rate code must be 6 characters or fewer.",
  createValidationNameRequired: "The new rate needs a name.",
  createValidationCategoryRequired: "Choose a room category for the new rate.",
  createValidationCurrencyRequired: "Choose a currency for the new rate.",
  createValidationCurrencyInvalid: "The selected currency is not available in the currencies catalog.",
  createValidationAdultsInvalid: "Enter a valid adults value for the new rate.",
  createValidationAdultsCap: (limit) => `Adults cannot exceed ${limit} for the selected category.`,
  createValidationChildrenInvalid: "Enter a valid children value for the new rate.",
  createValidationChildrenCap: (limit) => `Children cannot exceed ${limit} for the selected category.`,
  createValidationMaxOccupancyInvalid: "Enter a valid max occupancy for the new rate.",
  createValidationMaxOccupancyCap: (limit) => `Max occupancy cannot exceed ${limit} for the selected category.`,
  createValidationPrice:
    "Minimum and maximum price must be valid non-negative numbers, and min cannot exceed max.",
  createValidationPricePrecision: (currency, minorUnits) =>
    `${currency} prices accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`,
  createValidationPenaltyAmount: "Penalty amount must be a valid non-negative number.",
  createValidationPenaltyPrecision: (currency, minorUnits) =>
    `${currency} fixed penalties accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`,
  createValidationMinStay: "Enter a valid default min stay for the new rate.",
  createValidationMaxStay: "Enter a valid default max stay for the new rate.",
  createValidationStayRange: "Default min stay cannot be greater than default max stay.",
  createValidationWeekdayModeEmpty:
    "If you choose weekday stay rules, enter at least one weekday min or max stay value.",
  createValidationWeekdayMin: (dayLabel) => `${dayLabel}: min stay must be an integer greater than zero.`,
  createValidationWeekdayMax: (dayLabel) => `${dayLabel}: max stay must be an integer greater than zero.`,
  createValidationWeekdayRange: (dayLabel) => `${dayLabel}: min stay cannot be greater than max stay.`,
  validationRateCodeRequired: (index) => `Rate ${index} needs a code.`,
  validationRateCodeLength: (index) => `Rate ${index} code must be 6 characters or fewer.`,
  validationNameRequired: (index) => `Rate ${index} needs a name.`,
  validationCategoryRequired: (index) => `Rate ${index} needs a room category.`,
  validationCurrencyRequired: (index) => `Rate ${index} needs a currency.`,
  validationCurrencyInvalid: (index) => `Rate ${index} uses a currency that is not available in the catalog.`,
  validationAdultsInvalid: (index) => `Rate ${index} needs a valid adults value.`,
  validationAdultsCap: (index, limit) => `Rate ${index} cannot exceed ${limit} adults for its category.`,
  validationChildrenInvalid: (index) => `Rate ${index} needs a valid children value.`,
  validationChildrenCap: (index, limit) => `Rate ${index} cannot exceed ${limit} children for its category.`,
  validationMaxOccupancyInvalid: (index) => `Rate ${index} needs a valid max occupancy value.`,
  validationMaxOccupancyCap: (index, limit) => `Rate ${index} cannot exceed ${limit} max occupancy for its category.`,
  validationPrice: (index) =>
    `Rate ${index} has invalid minimum or maximum price values, or min is greater than max.`,
  validationPricePrecision: (index, currency, minorUnits) =>
    `Rate ${index}: ${currency} prices accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`,
  validationPenaltyAmount: (index) => `Rate ${index} has an invalid penalty amount.`,
  validationPenaltyPrecision: (index, currency, minorUnits) =>
    `Rate ${index}: ${currency} fixed penalties accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`,
  validationMinStay: (index) => `Rate ${index} needs a valid default min stay.`,
  validationMaxStay: (index) => `Rate ${index} needs a valid default max stay.`,
  validationStayRange: (index) => `Rate ${index} has a default min stay greater than default max stay.`,
  validationWeekdayModeEmpty: (index) =>
    `Rate ${index} is using weekday stay rules but has no weekday values configured.`,
  validationWeekdayMin: (index, dayLabel) => `Rate ${index} has an invalid weekday min stay for ${dayLabel}.`,
  validationWeekdayMax: (index, dayLabel) => `Rate ${index} has an invalid weekday max stay for ${dayLabel}.`,
  validationWeekdayRange: (index, dayLabel) =>
    `Rate ${index} has a weekday min stay greater than max stay for ${dayLabel}.`,
  summaryTitle: "A few quick tips",
  summaryIntro: "Keep the first room-rates draft practical. You can refine strategy later.",
  summaryPoints: [
    "Use short codes your team already recognizes.",
    "Mirror occupancy and stay rules carefully when categories have tighter limits.",
    "Capture cancellation and booking-engine visibility now so later setup stays cleaner.",
  ],
  summaryRatesLabel: "Rates in this step",
  summaryCategoriesLabel: "Categories used",
  summarySavedLabel: "Last save",
  summaryNotSaved: "Not saved yet",
  summaryNoClient: "No Agency or OTA",
  summaryNoCategory: "No category yet",
  summaryNoCurrency: "No currency",
  summaryAny: "Any",
  summaryNone: "None",
  summaryAdultsLine: (value, matchLabel) => `Adults: ${value === "" ? "Any" : `${matchLabel} ${value}`}`,
  summaryChildrenLine: (value, matchLabel) => `Children: ${value === "" ? "Any" : `${matchLabel} ${value}`}`,
  summaryMaxOccupancyLine: (value, matchLabel) =>
    `Max occupancy: ${value === "" ? "Any" : `${matchLabel} ${value}`}`,
  summaryMinStayLine: (value) => `Min stay: ${value === "" ? "None" : value}`,
  summaryMaxStayLine: (value) => `Max stay: ${value === "" ? "None" : value}`,
  summaryWeekdayMinLine: (value) => `Min by day: ${value}`,
  summaryWeekdayMaxLine: (value) => `Max by day: ${value}`,
  summaryPenaltyLine: (typeLabel, amount) =>
    amount === "" || amount === null ? "Penalty: None" : `Penalty: ${typeLabel} ${amount}`,
  summaryCancellationLine: (hasPolicy) => `Cancellation policy: ${hasPolicy ? "Set" : "None"}`,
  summaryVisibilityLine: (bookingLabel) => `Booking engine: ${bookingLabel}`,
  untitledRate: "Untitled rate",
  weekdays: [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" },
  ],
};

const ES_GRID = {
  ...BASE_GRID,
  title: "Configura tus room rates en una parrilla practica",
  copy: "Crea cada tarifa desde un modal guiado y revisalo todo despues desde una parrilla descriptiva antes de guardar.",
  noCategoriesTitle: "Completa antes las categorias de habitaciones",
  noCategoriesCopy: "Necesitamos categorias de habitaciones antes de preparar los room rates.",
  add: "Anadir tarifa",
  edit: "Editar",
  remove: "Eliminar",
  rateCode: "Codigo",
  rate: "Tarifa",
  name: "Nombre",
  roomCategory: "Categoria",
  client: "Agencia u OTA",
  currency: "Moneda",
  adults: "Adultos",
  children: "Ninos",
  maxOccupancy: "Max ocupacion",
  minPriceAmount: "Precio minimo",
  maxPriceAmount: "Precio maximo",
  occupancy: "Ocupacion",
  stayRules: "Estancia",
  visibility: "Visibilidad",
  policy: "Politica",
  actions: "Acciones",
  refundable: "Reembolsable",
  showInBookingEngine: "Motor de reservas",
  cancellationPolicy: "Politica de cancelacion",
  penaltyType: "Tipo de penalizacion",
  penaltyAmount: "Importe de penalizacion",
  penaltyDescription: "Descripcion de penalizacion",
  roomCategoryPlaceholder: "Elige categoria",
  clientPlaceholder: "Sin agencia u OTA",
  currencyPlaceholder: "Elige moneda",
  minStayPlaceholder: "Sin defecto",
  maxStayPlaceholder: "Sin defecto",
  minPricePlaceholder: "Sin limite",
  maxPricePlaceholder: "Sin limite",
  adultsAny: "Cualquiera",
  childrenAny: "Cualquiera",
  maxOccupancyAny: "Cualquiera",
  matchExact: "Exacto",
  matchUpto: "Hasta",
  refundableYes: "Si",
  bookingEngineVisible: "Visible",
  bookingEngineHidden: "Oculta",
  penaltyTypeNone: "Ninguna",
  penaltyTypeFixed: "Fija",
  penaltyTypePercent: "Porcentaje",
  emptyTitle: "Empieza con tu primera tarifa",
  emptyCopy: "Ejemplos: BAR, No reembolsable o Alojamiento y desayuno.",
  createModalTitle: "Crear una tarifa",
  createModalCopy: "Completa aqui los datos principales de la tarifa y despues anadela a la parrilla.",
  editModalTitle: (name) => `Editar ${name || "esta tarifa"}`,
  editModalCopy: "Revisa y ajusta aqui los datos de esta tarifa.",
  createModalHint: "",
  createModalCancel: "Cancelar",
  createModalConfirm: "Anadir a la parrilla",
  editModalConfirm: "Actualizar tarifa",
  sectionBasics: "Base",
  sectionBasicsCopy: "Codigo, nombre, categoria, agencia u OTA y moneda.",
  sectionVisibility: "Visibilidad",
  sectionVisibilityCopy: "Controla la exposicion en booking engine de la tarifa permanente.",
  sectionVisibilityHelp:
    "Aqui crearas las tarifas permanentes. Se guardaran con la fecha del dia en valida desde y 31/12/2099 en valida hasta. Mas adelante podras crear tarifas temporales.",
  sectionVisibilityHelpAria: "Mas informacion sobre las tarifas permanentes",
  bookingEngineHelpAria: "Mas informacion sobre la visibilidad en el motor de reservas",
  bookingEngineHelp:
    "Si esta tarifa es visible, se podra vender en tu web a traves del motor de reservas.\n\nSi no es visible, no se podra vender en tu web.\n\nRecomendacion: si esta tarifa es para una agencia u OTA, dejala como no visible para evitar que se venda en tu web con precio de agencia u OTA.",
  sectionOccupancy: "Limites de ocupacion",
  sectionOccupancyCopy:
    "Define adultos, ninos y ocupacion maxima uniendo el numero y la regla de comparacion en el mismo bloque.",
  adultsHelpAria: "Mas informacion sobre la regla de adultos",
  adultsHelp:
    "Usa esto para decidir como debe encajar la tarifa con la ocupacion de adultos dentro de la categoria elegida.\n\nElige el numero y despues si debe ser exacto o aplicar hasta ese valor.\n\nDejalo en Cualquiera si la tarifa puede venderse para cualquier ocupacion valida de adultos permitida en la categoria.",
  childrenHelpAria: "Mas informacion sobre la regla de ninos",
  childrenHelp:
    "Usa esto para decidir como debe encajar la tarifa con la ocupacion de ninos dentro de la categoria elegida.\n\nElige el numero y despues si debe ser exacto o aplicar hasta ese valor.\n\nDejalo en Cualquiera si la tarifa puede venderse para cualquier ocupacion valida de ninos permitida en la categoria.",
  maxOccupancyHelpAria: "Mas informacion sobre la ocupacion total",
  maxOccupancyHelp:
    "Usa esto cuando la tarifa deba depender del total de personas, sumando adultos y ninos.\n\nElige el numero y despues si debe ser exacto o aplicar hasta ese valor.\n\nDejalo en Cualquiera si la tarifa puede venderse para cualquier ocupacion total valida permitida en la categoria.",
  sectionPriceAndStay: "Precio y estancia",
  sectionPriceAndStayCopy:
    "Ajusta los umbrales de precio y elige un solo metodo de estancia para la tarifa permanente.",
  minPriceHelpAria: "Mas informacion sobre el precio minimo",
  minPriceHelp:
    "Es el precio minimo al que quieres vender.\n\nCualquier precio que quede por debajo de este limite no pasara y se ajustara automaticamente hasta este minimo.",
  maxPriceHelpAria: "Mas informacion sobre el precio maximo",
  maxPriceHelp:
    "Es el precio maximo al que quieres vender.\n\nCualquier precio que quede por encima de este limite no pasara y se ajustara automaticamente hasta este maximo.",
  sectionPolicies: "Politicas",
  sectionPoliciesCopy: "Anade aqui los datos de cancelacion y penalizacion si quieres dejarlos preparados ahora.",
  stayModeDefault: "Usar min/max stay por defecto",
  stayModeWeekday: "Usar min/max stay por dia",
  stayModeHint: "Elige solo un metodo: un par por defecto o reglas especificas por dia.",
  weekdayFillMin: "Rellenar min desde los defaults ocultos",
  weekdayFillMax: "Rellenar max desde los defaults ocultos",
  weekdayClear: "Limpiar dias",
  addSuccess: "La tarifa se ha anadido a la parrilla. Recuerda guardar cuando termines.",
  editSuccess: "La tarifa se ha actualizado en la parrilla. Recuerda guardar cuando termines.",
  loadError: "No hemos podido cargar los room rates guardados.",
  saveError: "No hemos podido guardar los room rates ahora mismo.",
  saveSuccess: "El borrador de room rates se ha guardado correctamente.",
  createValidationRateCodeRequired: "La nueva tarifa necesita un codigo.",
  createValidationRateCodeLength: "El codigo de la nueva tarifa debe tener 6 caracteres o menos.",
  createValidationNameRequired: "La nueva tarifa necesita un nombre.",
  createValidationCategoryRequired: "Elige una categoria para la nueva tarifa.",
  createValidationCurrencyRequired: "Elige una moneda para la nueva tarifa.",
  createValidationCurrencyInvalid: "La moneda seleccionada no esta disponible en el catalogo de monedas.",
  createValidationAdultsInvalid: "Introduce un valor valido de adultos para la nueva tarifa.",
  createValidationAdultsCap: (limit) => `Los adultos no pueden superar ${limit} en la categoria elegida.`,
  createValidationChildrenInvalid: "Introduce un valor valido de ninos para la nueva tarifa.",
  createValidationChildrenCap: (limit) => `Los ninos no pueden superar ${limit} en la categoria elegida.`,
  createValidationMaxOccupancyInvalid: "Introduce una ocupacion maxima valida para la nueva tarifa.",
  createValidationMaxOccupancyCap: (limit) =>
    `La ocupacion maxima no puede superar ${limit} en la categoria elegida.`,
  createValidationPrice:
    "El precio minimo y el maximo deben ser validos, y el minimo no puede superar al maximo.",
  createValidationPricePrecision: (currency, minorUnits) =>
    `Los precios en ${currency} admiten ${minorUnits === 0 ? "solo unidades enteras" : `hasta ${minorUnits} decimales`}.`,
  createValidationPenaltyAmount: "El importe de penalizacion debe ser un numero valido no negativo.",
  createValidationPenaltyPrecision: (currency, minorUnits) =>
    `Las penalizaciones fijas en ${currency} admiten ${minorUnits === 0 ? "solo unidades enteras" : `hasta ${minorUnits} decimales`}.`,
  createValidationMinStay: "Introduce un min stay por defecto valido para la nueva tarifa.",
  createValidationMaxStay: "Introduce un max stay por defecto valido para la nueva tarifa.",
  createValidationStayRange: "El min stay por defecto no puede ser mayor que el max stay por defecto.",
  createValidationWeekdayModeEmpty:
    "Si eliges reglas de estancia por dia, introduce al menos un valor de min o max stay en algun dia.",
  createValidationWeekdayMin: (dayLabel) => `${dayLabel}: el min stay debe ser un entero mayor que cero.`,
  createValidationWeekdayMax: (dayLabel) => `${dayLabel}: el max stay debe ser un entero mayor que cero.`,
  createValidationWeekdayRange: (dayLabel) => `${dayLabel}: el min stay no puede ser mayor que el max stay.`,
  validationRateCodeRequired: (index) => `La tarifa ${index} necesita un codigo.`,
  validationRateCodeLength: (index) => `La tarifa ${index} debe tener un codigo de 6 caracteres o menos.`,
  validationNameRequired: (index) => `La tarifa ${index} necesita un nombre.`,
  validationCategoryRequired: (index) => `La tarifa ${index} necesita una categoria.`,
  validationCurrencyRequired: (index) => `La tarifa ${index} necesita una moneda.`,
  validationCurrencyInvalid: (index) => `La tarifa ${index} usa una moneda que no esta disponible en el catalogo.`,
  validationAdultsInvalid: (index) => `La tarifa ${index} necesita un valor valido de adultos.`,
  validationAdultsCap: (index, limit) => `La tarifa ${index} no puede superar ${limit} adultos en su categoria.`,
  validationChildrenInvalid: (index) => `La tarifa ${index} necesita un valor valido de ninos.`,
  validationChildrenCap: (index, limit) => `La tarifa ${index} no puede superar ${limit} ninos en su categoria.`,
  validationMaxOccupancyInvalid: (index) => `La tarifa ${index} necesita una ocupacion maxima valida.`,
  validationMaxOccupancyCap: (index, limit) =>
    `La tarifa ${index} no puede superar ${limit} de ocupacion maxima en su categoria.`,
  validationPrice: (index) =>
    `La tarifa ${index} tiene precios minimo o maximo no validos, o el minimo es mayor que el maximo.`,
  validationPricePrecision: (index, currency, minorUnits) =>
    `Tarifa ${index}: los precios en ${currency} admiten ${minorUnits === 0 ? "solo unidades enteras" : `hasta ${minorUnits} decimales`}.`,
  validationPenaltyAmount: (index) => `La tarifa ${index} tiene un importe de penalizacion no valido.`,
  validationPenaltyPrecision: (index, currency, minorUnits) =>
    `Tarifa ${index}: las penalizaciones fijas en ${currency} admiten ${minorUnits === 0 ? "solo unidades enteras" : `hasta ${minorUnits} decimales`}.`,
  validationMinStay: (index) => `La tarifa ${index} necesita un min stay por defecto valido.`,
  validationMaxStay: (index) => `La tarifa ${index} necesita un max stay por defecto valido.`,
  validationStayRange: (index) =>
    `La tarifa ${index} tiene un min stay por defecto mayor que el max stay por defecto.`,
  validationWeekdayModeEmpty: (index) =>
    `La tarifa ${index} usa reglas de estancia por dia pero no tiene ningun valor configurado.`,
  validationWeekdayMin: (index, dayLabel) =>
    `La tarifa ${index} tiene un min stay por dia no valido para ${dayLabel}.`,
  validationWeekdayMax: (index, dayLabel) =>
    `La tarifa ${index} tiene un max stay por dia no valido para ${dayLabel}.`,
  validationWeekdayRange: (index, dayLabel) =>
    `La tarifa ${index} tiene un min stay por dia mayor que el max stay para ${dayLabel}.`,
  summaryTitle: "Unas pistas rapidas",
  summaryIntro: "Haz el primer borrador de tarifas de una forma practica. Luego ya afinaremos estrategia.",
  summaryRatesLabel: "Tarifas en este paso",
  summaryCategoriesLabel: "Categorias usadas",
  summarySavedLabel: "Ultimo guardado",
  summaryNotSaved: "Todavia sin guardar",
  summaryNoClient: "Sin agencia u OTA",
  summaryNoCategory: "Todavia sin categoria",
  summaryNoCurrency: "Sin moneda",
  summaryAny: "Cualquiera",
  summaryNone: "Ninguna",
  summaryAdultsLine: (value, matchLabel) => `Adultos: ${value === "" ? "Cualquiera" : `${matchLabel} ${value}`}`,
  summaryChildrenLine: (value, matchLabel) => `Ninos: ${value === "" ? "Cualquiera" : `${matchLabel} ${value}`}`,
  summaryMaxOccupancyLine: (value, matchLabel) =>
    `Max ocupacion: ${value === "" ? "Cualquiera" : `${matchLabel} ${value}`}`,
  summaryMinStayLine: (value) => `Min stay: ${value === "" ? "Ninguno" : value}`,
  summaryMaxStayLine: (value) => `Max stay: ${value === "" ? "Ninguno" : value}`,
  summaryWeekdayMinLine: (value) => `Min por dia: ${value}`,
  summaryWeekdayMaxLine: (value) => `Max por dia: ${value}`,
  summaryPenaltyLine: (typeLabel, amount) =>
    amount === "" || amount === null ? "Penalizacion: ninguna" : `Penalizacion: ${typeLabel} ${amount}`,
  summaryCancellationLine: (hasPolicy) => `Cancelacion: ${hasPolicy ? "Definida" : "Ninguna"}`,
  summaryVisibilityLine: (bookingLabel) => `Booking engine: ${bookingLabel}`,
  untitledRate: "Tarifa sin nombre",
  weekdays: [
    { key: "monday", label: "Lunes" },
    { key: "tuesday", label: "Martes" },
    { key: "wednesday", label: "Miercoles" },
    { key: "thursday", label: "Jueves" },
    { key: "friday", label: "Viernes" },
    { key: "saturday", label: "Sabado" },
    { key: "sunday", label: "Domingo" },
  ],
};

const CA_GRID = {
  ...BASE_GRID,
  title: "Configura les teves room rates en una graella practica",
  copy: "Crea cada tarifa des d'un modal guiat i revisa-ho despres des d'una graella descriptiva abans de desar.",
  noCategoriesTitle: "Completa abans les categories d'habitacio",
  noCategoriesCopy: "Necessitem categories d'habitacio abans de preparar les room rates.",
  add: "Afegir tarifa",
  edit: "Editar",
  remove: "Eliminar",
  rateCode: "Codi",
  rate: "Tarifa",
  name: "Nom",
  roomCategory: "Categoria",
  client: "Agencia o OTA",
  currency: "Moneda",
  adults: "Adults",
  children: "Nens",
  maxOccupancy: "Max ocupacio",
  minPriceAmount: "Preu minim",
  maxPriceAmount: "Preu maxim",
  occupancy: "Ocupacio",
  stayRules: "Estada",
  visibility: "Visibilitat",
  policy: "Politica",
  actions: "Accions",
  refundable: "Reemborsable",
  showInBookingEngine: "Motor de reserves",
  cancellationPolicy: "Politica de cancel.lacio",
  penaltyType: "Tipus de penalitzacio",
  penaltyAmount: "Import de penalitzacio",
  penaltyDescription: "Descripcio de penalitzacio",
  roomCategoryPlaceholder: "Tria categoria",
  clientPlaceholder: "Sense agencia o OTA",
  currencyPlaceholder: "Tria moneda",
  minStayPlaceholder: "Sense defecte",
  maxStayPlaceholder: "Sense defecte",
  minPricePlaceholder: "Sense limit",
  maxPricePlaceholder: "Sense limit",
  adultsAny: "Qualsevol",
  childrenAny: "Qualsevol",
  maxOccupancyAny: "Qualsevol",
  matchExact: "Exacte",
  matchUpto: "Fins a",
  refundableYes: "Si",
  bookingEngineVisible: "Visible",
  bookingEngineHidden: "Oculta",
  penaltyTypeNone: "Cap",
  penaltyTypeFixed: "Fixa",
  penaltyTypePercent: "Percentatge",
  emptyTitle: "Comenca amb la teva primera tarifa",
  emptyCopy: "Exemples: BAR, No reemborsable o Allotjament i esmorzar.",
  createModalTitle: "Crear una tarifa",
  createModalCopy: "Completa aqui les dades principals de la tarifa i despres afegeix-la a la graella.",
  editModalTitle: (name) => `Editar ${name || "aquesta tarifa"}`,
  editModalCopy: "Revisa i ajusta aqui les dades d'aquesta tarifa.",
  createModalHint: "",
  createModalCancel: "Cancelar",
  createModalConfirm: "Afegir a la graella",
  editModalConfirm: "Actualitzar tarifa",
  sectionBasics: "Base",
  sectionBasicsCopy: "Codi, nom, categoria, agencia o OTA i moneda.",
  sectionVisibility: "Visibilitat",
  sectionVisibilityCopy: "Controla l'exposicio al booking engine de la tarifa permanent.",
  sectionVisibilityHelp:
    "Aqui crearas les tarifes permanents. Es desaran amb la data del dia a valida des i 31/12/2099 a valida fins. Mes endavant podras crear tarifes temporals.",
  sectionVisibilityHelpAria: "Mes informacio sobre les tarifes permanents",
  bookingEngineHelpAria: "Mes informacio sobre la visibilitat al motor de reserves",
  bookingEngineHelp:
    "Si aquesta tarifa es visible, es podra vendre a la teva web a traves del motor de reserves.\n\nSi no es visible, no es podra vendre a la teva web.\n\nRecomanacio: si aquesta tarifa es per a una agencia o OTA, deixa-la com a no visible per evitar que es vengui a la teva web amb preu d'agencia o OTA.",
  sectionOccupancy: "Limits d'ocupacio",
  sectionOccupancyCopy:
    "Defineix adults, nens i ocupacio maxima ajuntant el numero i la regla de comparacio dins del mateix bloc.",
  adultsHelpAria: "Mes informacio sobre la regla d'adults",
  adultsHelp:
    "Fes servir aixo per decidir com ha d'encaixar la tarifa amb l'ocupacio d'adults dins de la categoria triada.\n\nTria el numero i despres si ha de ser exacte o aplicar fins a aquest valor.\n\nDeixa-ho a Qualsevol si la tarifa es pot vendre per a qualsevol ocupacio valida d'adults permesa a la categoria.",
  childrenHelpAria: "Mes informacio sobre la regla de nens",
  childrenHelp:
    "Fes servir aixo per decidir com ha d'encaixar la tarifa amb l'ocupacio de nens dins de la categoria triada.\n\nTria el numero i despres si ha de ser exacte o aplicar fins a aquest valor.\n\nDeixa-ho a Qualsevol si la tarifa es pot vendre per a qualsevol ocupacio valida de nens permesa a la categoria.",
  maxOccupancyHelpAria: "Mes informacio sobre l'ocupacio total",
  maxOccupancyHelp:
    "Fes servir aixo quan la tarifa hagi de dependre del total de persones, sumant adults i nens.\n\nTria el numero i despres si ha de ser exacte o aplicar fins a aquest valor.\n\nDeixa-ho a Qualsevol si la tarifa es pot vendre per a qualsevol ocupacio total valida permesa a la categoria.",
  sectionPriceAndStay: "Preu i estada",
  sectionPriceAndStayCopy:
    "Ajusta els llindars de preu i tria un sol metode d'estada per a la tarifa permanent.",
  minPriceHelpAria: "Mes informacio sobre el preu minim",
  minPriceHelp:
    "Es el preu minim al qual vols vendre.\n\nQualsevol preu que quedi per sota d'aquest limit no passara i s'ajustara automaticament fins a aquest minim.",
  maxPriceHelpAria: "Mes informacio sobre el preu maxim",
  maxPriceHelp:
    "Es el preu maxim al qual vols vendre.\n\nQualsevol preu que quedi per sobre d'aquest limit no passara i s'ajustara automaticament fins a aquest maxim.",
  sectionPolicies: "Politiques",
  sectionPoliciesCopy: "Afegeix aqui les dades de cancel.lacio i penalitzacio si les vols deixar preparades ara.",
  stayModeDefault: "Usar min/max stay per defecte",
  stayModeWeekday: "Usar min/max stay per dia",
  stayModeHint: "Tria nomes un metode: un parell per defecte o regles especifiques per dia.",
  weekdayFillMin: "Omplir min des dels defaults ocults",
  weekdayFillMax: "Omplir max des dels defaults ocults",
  weekdayClear: "Netejar dies",
  addSuccess: "La tarifa s'ha afegit a la graella. Recorda desar quan acabis.",
  editSuccess: "La tarifa s'ha actualitzat a la graella. Recorda desar quan acabis.",
  loadError: "No hem pogut carregar les room rates desades.",
  saveError: "No hem pogut desar les room rates ara mateix.",
  saveSuccess: "L'esborrany de room rates s'ha desat correctament.",
  createValidationRateCodeRequired: "La nova tarifa necessita un codi.",
  createValidationRateCodeLength: "El codi de la nova tarifa ha de tenir 6 caracters o menys.",
  createValidationNameRequired: "La nova tarifa necessita un nom.",
  createValidationCategoryRequired: "Tria una categoria per a la nova tarifa.",
  createValidationCurrencyRequired: "Tria una moneda per a la nova tarifa.",
  createValidationCurrencyInvalid: "La moneda seleccionada no esta disponible al cataleg de monedes.",
  createValidationAdultsInvalid: "Introdueix un valor valid d'adults per a la nova tarifa.",
  createValidationAdultsCap: (limit) => `Els adults no poden superar ${limit} a la categoria triada.`,
  createValidationChildrenInvalid: "Introdueix un valor valid de nens per a la nova tarifa.",
  createValidationChildrenCap: (limit) => `Els nens no poden superar ${limit} a la categoria triada.`,
  createValidationMaxOccupancyInvalid: "Introdueix una ocupacio maxima valida per a la nova tarifa.",
  createValidationMaxOccupancyCap: (limit) =>
    `L'ocupacio maxima no pot superar ${limit} a la categoria triada.`,
  createValidationPrice:
    "El preu minim i el maxim han de ser valids, i el minim no pot superar el maxim.",
  createValidationPricePrecision: (currency, minorUnits) =>
    `Els preus en ${currency} admeten ${minorUnits === 0 ? "nomes unitats senceres" : `fins a ${minorUnits} decimals`}.`,
  createValidationPenaltyAmount: "L'import de penalitzacio ha de ser un numero valid no negatiu.",
  createValidationPenaltyPrecision: (currency, minorUnits) =>
    `Les penalitzacions fixes en ${currency} admeten ${minorUnits === 0 ? "nomes unitats senceres" : `fins a ${minorUnits} decimals`}.`,
  createValidationMinStay: "Introdueix un min stay per defecte valid per a la nova tarifa.",
  createValidationMaxStay: "Introdueix un max stay per defecte valid per a la nova tarifa.",
  createValidationStayRange: "El min stay per defecte no pot ser mes gran que el max stay per defecte.",
  createValidationWeekdayModeEmpty:
    "Si tries regles d'estada per dia, introdueix almenys un valor de min o max stay en algun dia.",
  createValidationWeekdayMin: (dayLabel) => `${dayLabel}: el min stay ha de ser un enter mes gran que zero.`,
  createValidationWeekdayMax: (dayLabel) => `${dayLabel}: el max stay ha de ser un enter mes gran que zero.`,
  createValidationWeekdayRange: (dayLabel) => `${dayLabel}: el min stay no pot ser mes gran que el max stay.`,
  validationRateCodeRequired: (index) => `La tarifa ${index} necessita un codi.`,
  validationRateCodeLength: (index) => `La tarifa ${index} ha de tenir un codi de 6 caracters o menys.`,
  validationNameRequired: (index) => `La tarifa ${index} necessita un nom.`,
  validationCategoryRequired: (index) => `La tarifa ${index} necessita una categoria.`,
  validationCurrencyRequired: (index) => `La tarifa ${index} necessita una moneda.`,
  validationCurrencyInvalid: (index) => `La tarifa ${index} fa servir una moneda que no esta disponible al cataleg.`,
  validationAdultsInvalid: (index) => `La tarifa ${index} necessita un valor valid d'adults.`,
  validationAdultsCap: (index, limit) => `La tarifa ${index} no pot superar ${limit} adults a la seva categoria.`,
  validationChildrenInvalid: (index) => `La tarifa ${index} necessita un valor valid de nens.`,
  validationChildrenCap: (index, limit) => `La tarifa ${index} no pot superar ${limit} nens a la seva categoria.`,
  validationMaxOccupancyInvalid: (index) => `La tarifa ${index} necessita una ocupacio maxima valida.`,
  validationMaxOccupancyCap: (index, limit) =>
    `La tarifa ${index} no pot superar ${limit} d'ocupacio maxima a la seva categoria.`,
  validationPrice: (index) =>
    `La tarifa ${index} te preus minim o maxim no valids, o el minim es mes gran que el maxim.`,
  validationPricePrecision: (index, currency, minorUnits) =>
    `Tarifa ${index}: els preus en ${currency} admeten ${minorUnits === 0 ? "nomes unitats senceres" : `fins a ${minorUnits} decimals`}.`,
  validationPenaltyAmount: (index) => `La tarifa ${index} te un import de penalitzacio no valid.`,
  validationPenaltyPrecision: (index, currency, minorUnits) =>
    `Tarifa ${index}: les penalitzacions fixes en ${currency} admeten ${minorUnits === 0 ? "nomes unitats senceres" : `fins a ${minorUnits} decimals`}.`,
  validationMinStay: (index) => `La tarifa ${index} necessita un min stay per defecte valid.`,
  validationMaxStay: (index) => `La tarifa ${index} necessita un max stay per defecte valid.`,
  validationStayRange: (index) =>
    `La tarifa ${index} te un min stay per defecte mes gran que el max stay per defecte.`,
  validationWeekdayModeEmpty: (index) =>
    `La tarifa ${index} fa servir regles d'estada per dia pero no te cap valor configurat.`,
  validationWeekdayMin: (index, dayLabel) =>
    `La tarifa ${index} te un min stay per dia no valid per a ${dayLabel}.`,
  validationWeekdayMax: (index, dayLabel) =>
    `La tarifa ${index} te un max stay per dia no valid per a ${dayLabel}.`,
  validationWeekdayRange: (index, dayLabel) =>
    `La tarifa ${index} te un min stay per dia mes gran que el max stay per a ${dayLabel}.`,
  summaryTitle: "Algunes pistes rapides",
  summaryIntro: "Fes el primer esborrany de tarifes d'una manera practica. Despres ja afinarem l'estrategia.",
  summaryRatesLabel: "Tarifes en aquest pas",
  summaryCategoriesLabel: "Categories usades",
  summarySavedLabel: "Ultim desat",
  summaryNotSaved: "Encara sense desar",
  summaryNoClient: "Sense agencia o OTA",
  summaryNoCategory: "Encara sense categoria",
  summaryNoCurrency: "Sense moneda",
  summaryAny: "Qualsevol",
  summaryNone: "Cap",
  summaryAdultsLine: (value, matchLabel) => `Adults: ${value === "" ? "Qualsevol" : `${matchLabel} ${value}`}`,
  summaryChildrenLine: (value, matchLabel) => `Nens: ${value === "" ? "Qualsevol" : `${matchLabel} ${value}`}`,
  summaryMaxOccupancyLine: (value, matchLabel) =>
    `Max ocupacio: ${value === "" ? "Qualsevol" : `${matchLabel} ${value}`}`,
  summaryMinStayLine: (value) => `Min stay: ${value === "" ? "Cap" : value}`,
  summaryMaxStayLine: (value) => `Max stay: ${value === "" ? "Cap" : value}`,
  summaryWeekdayMinLine: (value) => `Min per dia: ${value}`,
  summaryWeekdayMaxLine: (value) => `Max per dia: ${value}`,
  summaryPenaltyLine: (typeLabel, amount) =>
    amount === "" || amount === null ? "Penalitzacio: cap" : `Penalitzacio: ${typeLabel} ${amount}`,
  summaryCancellationLine: (hasPolicy) => `Cancel.lacio: ${hasPolicy ? "Definida" : "Cap"}`,
  summaryVisibilityLine: (bookingLabel) => `Booking engine: ${bookingLabel}`,
  untitledRate: "Tarifa sense nom",
  weekdays: [
    { key: "monday", label: "Dilluns" },
    { key: "tuesday", label: "Dimarts" },
    { key: "wednesday", label: "Dimecres" },
    { key: "thursday", label: "Dijous" },
    { key: "friday", label: "Divendres" },
    { key: "saturday", label: "Dissabte" },
    { key: "sunday", label: "Diumenge" },
  ],
};

const ROOM_RATES_MESSAGES = {
  en: { grid: BASE_GRID },
  es: { grid: ES_GRID },
  ca: { grid: CA_GRID },
};

export function getRoomRatesMessages(languageId) {
  return ROOM_RATES_MESSAGES[languageId] || ROOM_RATES_MESSAGES.en;
}
